import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Login
      const response = await axios.post(`${API}/auth/login`, { identifier, password });
      const { token, user } = response.data;
      
      // Check if user is admin
      if (!user.is_admin) {
        toast.error("Access denied. Admin privileges required.");
        setLoading(false);
        return;
      }
      
      // Store token and redirect to admin
      localStorage.setItem("pourcircle_token", token);
      toast.success("Welcome, Admin!");
      
      // Force page reload to update auth context, then go to admin
      window.location.href = "/admin";
      
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to main site */}
        <button 
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to PourCircle
        </button>

        <div className="glass-card p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              Admin Portal
            </h1>
            <p className="text-white/60 mt-2">Sign in with your admin credentials</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                Username or Email
              </label>
              <Input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Enter your username or email"
                className="input-dark"
                required
                data-testid="admin-login-identifier"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                Password
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="input-dark"
                required
                data-testid="admin-login-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full mt-6"
              data-testid="admin-login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Sign In to Admin
                </>
              )}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-center text-white/40 text-xs mt-6">
            This portal is for authorized administrators only.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
